// ============================================================================
// CORE API CLIENT - Domain-Specific Data Flow Foundation
// ============================================================================
//
// This is the centralized API client that provides:
// 1. Enterprise-grade authentication middleware
// 2. Global error handling with automatic token refresh
// 3. Enterprise logging for compliance auditing
// 4. Request/response interceptors for security
//
// Each domain API should extend this base client to ensure consistent
// behavior across the application.
// ============================================================================

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// ============================================================================
// TYPES
// ============================================================================

export interface ApiClientConfig {
  baseURL?: string;
  timeout?: number;
  enableLogging?: boolean;
}

export interface SecurityLogEntry {
  timestamp: string;
  event_type: string;
  user_id?: string | number;
  details?: {
    error_id?: string;
    status?: number;
    url?: string;
    method?: string;
    message?: string;
  };
}

// ============================================================================
// CORE API CLIENT CLASS
// ============================================================================

export class ApiClient {
  protected client: AxiosInstance;
  protected requestCount = 0;
  protected errorCount = 0;
  protected enableLogging: boolean;

  constructor(config: ApiClientConfig = {}) {
    this.enableLogging = config.enableLogging ?? import.meta.env.DEV;

    // Use direct backend URL in development to bypass Vite proxy issues with multipart
    const baseURL = config.baseURL || (import.meta.env.DEV ? 'http://localhost:3001/api' : '/api');

    this.client = axios.create({
      baseURL,
      timeout: config.timeout ?? 0, // No timeout by default, can be overridden
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      withCredentials: true, // Send httpOnly cookies cross-origin
      // Remove default Content-Type to let axios set it based on data type
    });

    this.setupInterceptors();
  }

  // ============================================================================
  // INTERCEPTOR SETUP
  // ============================================================================

  private setupInterceptors(): void {
    // Request interceptor - Add auth token and enterprise logging
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        this.requestCount++;

        if (this.enableLogging) {
          console.log(`[API Request #${this.requestCount}]`, {
            method: config.method?.toUpperCase(),
            url: config.url,
            timestamp: new Date().toISOString(),
            domain: this.getDomainFromUrl(config.url || ''),
          });
        }

        // Add authentication token - prioritize impersonation token over regular token
        // Tokens are now in httpOnly cookies (sent automatically via withCredentials)
        // Keep header auth as fallback for SSE/EventSource and backward compatibility
        const impersonationToken = sessionStorage.getItem('impersonation_token');
        const regularToken = localStorage.getItem('token');
        const token = impersonationToken || regularToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // ── CSRF Token injection ────────────────────────────────────────────
        // Read non-httpOnly csrf_token cookie and inject as header on mutations
        const MUTATING = new Set(['post', 'put', 'patch', 'delete']);
        if (config.method && MUTATING.has(config.method.toLowerCase())) {
          const csrfToken = this.getCsrfCookie();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }

        // Add company_id from user context if available
        const user = this.getUserFromStorage();
        if (user?.company_id) {
          config.headers['X-Company-ID'] = user.company_id.toString();
        }

        return config;
      },
      (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle errors, token refresh, and logging
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        if (this.enableLogging) {
          console.log(`[API Response #${this.requestCount}]`, {
            status: response.status,
            statusText: response.statusText,
            timestamp: new Date().toISOString(),
            domain: this.getDomainFromUrl(response.config.url || ''),
          });
        }
        return response;
      },
      async (error: AxiosError) => {
        this.errorCount++;

        // Enhanced error logging for enterprise debugging
        const errorInfo = {
          errorId: `ERR_${this.errorCount}`,
          timestamp: new Date().toISOString(),
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          method: error.config?.method,
          message: (error.response?.data as any)?.message || error.message,
          stack: error.stack,
        };

        if (this.enableLogging) {
          console.error('[API Response Error]', errorInfo);
        }

        // Auth endpoints must NEVER trigger the token-refresh path — otherwise a
        // 401 from /auth/refresh recursively calls itself forever (the refresh
        // request carries no `_retry` flag, so the interceptor re-enters every time).
        const requestUrl = error.config?.url || '';
        const isAuthEndpoint = /\/auth\/(refresh|login|logout|login-2fa|verify-2fa|resend-2fa)/.test(requestUrl);

        if (error.response?.status === 401 && isAuthEndpoint && requestUrl.includes('/auth/refresh')) {
          // The refresh token itself is rejected — drop it so we stop retrying.
          localStorage.removeItem('refreshToken');
        }

        // Handle token refresh for 401 errors
        if (error.response?.status === 401 && !(error.config as any)?._retry && !isAuthEndpoint) {
          const config = error.config as any;
          config._retry = true;

          try {
            // Try to refresh the token
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              if (response.success && response.token) {
                localStorage.setItem('token', response.token);
                // Update the authorization header for the original request
                config.headers.Authorization = `Bearer ${response.token}`;
                // Retry the original request
                return this.client(config);
              }
            }
          } catch (refreshError) {
            console.error('[Token Refresh Failed]', refreshError);
          }

          // If refresh failed or no token, clear session and redirect
          console.warn('[Security] Authentication failed', {
            url: error.config?.url,
            method: error.config?.method,
          });

          // Check if this is a navigation request vs API request
          const isNavigationRequest = error.config?.url?.includes('/api/') === false;

          if (!isNavigationRequest) {
            this.clearSession();
            // Force page reload to clear all state and redirect to login
            window.location.reload();
          }
        } else if (error.response?.status === 403) {
          console.warn('[Security] Access forbidden - insufficient permissions');
          this.logSecurityEvent({
            ...errorInfo,
            event_type: 'access_denied',
          });
        } else if (error.response?.status === 404) {
          console.warn('[API] Resource not found');
        } else if (error.response?.status === 422) {
          console.warn('[Validation] Request validation failed');
        } else if (error.response?.status === 429) {
          console.warn('[Rate Limit] Too many requests');
        } else if (error.response?.status && error.response.status >= 500) {
          console.error('[Server] Internal server error');
        }

        // Log to audit service for enterprise compliance
        this.logSecurityEvent({
          ...errorInfo,
          event_type: 'api_error',
        });

        return Promise.reject(error);
      }
    );
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private getDomainFromUrl(url: string): string {
    const match = url.match(/^\/api\/([^\/]+)/);
    return match?.[1] || 'unknown';
  }

  /** Read the non-httpOnly CSRF cookie set by the server */
  private getCsrfCookie(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    return match ? decodeURIComponent(match[1] as string) : null;
  }

  private getUserFromStorage(): any {
    try {
      // Check for impersonation user first, then fall back to regular user
      const impersonationUserStr = sessionStorage.getItem('impersonation_user');
      if (impersonationUserStr) {
        return JSON.parse(impersonationUserStr);
      }

      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  private clearSession(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Also clear impersonation data
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonation_user');
  }

  // ============================================================================
  // SECURITY LOGGING
  // ============================================================================

  private logSecurityEvent(event: SecurityLogEntry): void {
    try {
      const user = this.getUserFromStorage();
      const securityLog: SecurityLogEntry = {
        timestamp: event.timestamp,
        event_type: event.event_type,
        user_id: user?.id || 'anonymous',
        ...(event.details && { details: event.details }),
      };

      // Store security events for compliance auditing
      const existingLogs = JSON.parse(localStorage.getItem('security_logs') || '[]');
      existingLogs.push(securityLog);

      // Keep only last 100 events to prevent storage bloat
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }

      localStorage.setItem('security_logs', JSON.stringify(existingLogs));
    } catch (error) {
      console.error('[Security Logging Failed]', error);
    }
  }

  // ============================================================================
  // TOKEN REFRESH
  // ============================================================================

  private async refreshToken(token: string): Promise<{ success: boolean; token?: string; message?: string }> {
    try {
      // Use a bare axios call (not this.client) so the response interceptor's
      // 401 handler can never recurse back into this method.
      const response = await axios.post(
        `${this.client.defaults.baseURL ?? '/api'}/auth/refresh`,
        { refreshToken: token },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      return { success: false, message: 'Token refresh failed' };
    }
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Get the underlying axios instance for advanced use cases
   */
  public getInstance(): AxiosInstance {
    return this.client;
  }

  /**
   * Enable or disable enterprise logging
   */
  public setLogging(enabled: boolean): void {
    this.enableLogging = enabled;
  }

  /**
   * Get security logs for compliance auditing
   */
  public getSecurityLogs(): SecurityLogEntry[] {
    try {
      return JSON.parse(localStorage.getItem('security_logs') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Clear security logs
   */
  public clearSecurityLogs(): void {
    localStorage.removeItem('security_logs');
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default ApiClient;

// Singleton instance for application-wide use
export const apiClient = new ApiClient();
